// src/components/chat/ModernChatInput.tsx
import * as React from "react";
import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { modernChatStyles } from "./styles/modernChatStyles";
import AgentSelector, { Agent } from "./AgentSelector";
import PlusMenuButton from "./PlusMenuButton";
import SettingsMenuButton from "./SettingsMenuButton";
import { useAgent } from "../../contexts/AgentContext";
import { useModel } from "../../contexts/ModelContext";
import { useEntitlements } from "../../hooks/useEntitlements";
import { createLogger } from "../../utils/logger";
import { createFileId } from "../../utils/fileUtils";
import StopGeneratingIcon from "../../../assets/StopGenerating.svg";
import { DocumentSuggestions } from "./DocumentSuggestions";
import { DocumentLibraryModal } from "./DocumentLibraryModal";
import { SharePointFile, EnhancedFile } from "../../interfaces/sharePointTypes";
import { useSharePoint } from "../../hooks/useSharePoint";
import CatylexLogo from "../CatylexLogo";
import TenantSelector from "./TenantSelector";
import { FEATURE_FLAGS } from "../../config/featureFlags";
import { useChatSettings } from "../../contexts/ChatSettingsContext";
import { useTenantSelectorAccess } from "../../hooks/useTenantSelectorAccess";
import ConciseModeBadge from "./badges/ConciseModeBadge";
import BenchmarkBadge from "./badges/BenchmarkBadge";
import { track } from "../../utils/analytics";

const log = createLogger("ModernChatInput");

interface ModernChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  placeholder?: string;
  onInsert?: () => void;
  canInsert?: boolean;
  isInserting?: boolean;
  showClearChat: boolean;
  onClearChat: () => void;
  containerHeight?: number;
  onFilesSelected?: (files: File[]) => void;
  onRemoveFile?: (fileId: string) => void; // NEW: Added file removal callback
}

// Expose imperative methods for parent components
export interface ModernChatInputRef {
  setInputValue: (value: string) => void;
  focusInput: () => void;
}

const ModernChatInput = forwardRef<ModernChatInputRef, ModernChatInputProps>(
  (
    {
      onSendMessage,
      isLoading,
      placeholder = "How can I help you today?",
      onInsert,
      canInsert = false,
      isInserting = false,
      showClearChat,
      onClearChat,
      containerHeight = 0,
      onFilesSelected,
      onRemoveFile, // NEW: Destructure the file removal callback
    },
    ref
  ) => {
    const [message, setMessage] = useState("");
    const [showDocumentSuggestions, setShowDocumentSuggestions] = useState(false);
    const [documentSuggestions, setDocumentSuggestions] = useState<SharePointFile[]>([]);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [selectedDocuments, setSelectedDocuments] = useState<SharePointFile[]>([]);
    const [showLibraryModal, setShowLibraryModal] = useState(false);
    // Track which SharePoint files are currently attached to carousel
    const [attachedSharePointFileIds, setAttachedSharePointFileIds] = useState<Set<string>>(
      new Set()
    );
    // Error toast for failed downloads
    const [downloadError, setDownloadError] = useState<string | null>(null);

    // Debounce timer for @ mention search
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // Expose imperative methods to parent via ref
    useImperativeHandle(ref, () => ({
      setInputValue: (value: string) => {
        setMessage(value);
        // Adjust textarea height after setting value
        setTimeout(() => adjustTextareaHeight(), 0);
      },
      focusInput: () => {
        textareaRef.current?.focus();
      },
    }));

    // SharePoint integration
    const { searchFiles, getRecentFiles, downloadFile, downloadFileAsFile } = useSharePoint();

    // Get current agent and available agents from context
    // FIXED: Now properly destructuring availableAgents and currentAgentFeatures
    const { currentAgent, setCurrentAgent, availableAgents, currentAgentFeatures } = useAgent();

    // Document Library and the concise-mode control are per-tenant
    // entitlements, not agent features
    const { documentLibrary: isDocumentLibraryEnabled, conciseMode: isConciseModeEnabled } =
      useEntitlements();

    // Get cancelGeneration from ModelContext for stop functionality
    const { cancelGeneration } = useModel();

    // Get chat settings for Benchmark Mode indicator
    const { settings, toggleBenchmarkMode, toggleConciseMode } = useChatSettings();

    // Check if current user has access to tenant selector
    const showTenantSelector = useTenantSelectorAccess();

    // Ref for the textarea element
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Track textarea's current height
    const [textareaHeight, setTextareaHeight] = useState<number | undefined>(undefined);

    // Function to adjust textarea height based on content
    const adjustTextareaHeight = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate content height
      const contentHeight = textarea.scrollHeight;

      // Calculate max height (50% of container or min 100px if container height not available)
      const maxHeight = containerHeight > 0 ? containerHeight * 0.5 : 200;

      // Set new height based on content, capped at max height
      const newHeight = Math.min(Math.max(contentHeight, 38), maxHeight);
      textarea.style.height = `${newHeight}px`;

      // Update state if height changed
      if (newHeight !== textareaHeight) {
        setTextareaHeight(newHeight);
      }
    };

    // Adjust height when message content changes
    useEffect(() => {
      adjustTextareaHeight();
    }, [message, containerHeight]);

    // Adjust on window resize
    useEffect(() => {
      const handleResize = () => adjustTextareaHeight();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Cleanup debounce timer on unmount
    useEffect(() => {
      return () => {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
      };
    }, []);

    // Clear attached file IDs when modal opens to ensure fresh state
    // This prevents showing stale state when files have been removed from carousel
    useEffect(() => {
      if (showLibraryModal) {
        log.info("Modal opened, clearing attached file ID tracking for fresh state");
        setAttachedSharePointFileIds(new Set());
      }
    }, [showLibraryModal]);

    const handleSubmit = () => {
      if (message.trim() && !isLoading) {
        onSendMessage(message);
        setMessage("");
        // Reset textarea height after sending message
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
          setTextareaHeight(undefined);
        }
      }
    };

    //Prevent submission while streaming
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Handle document suggestions navigation
      if (showDocumentSuggestions) {
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setSelectedSuggestionIndex((prev) => (prev + 1) % documentSuggestions.length);
            break;
          case "ArrowUp":
            e.preventDefault();
            setSelectedSuggestionIndex(
              (prev) => (prev - 1 + documentSuggestions.length) % documentSuggestions.length
            );
            break;
          case "Enter":
          case "Tab":
            if (documentSuggestions.length > 0) {
              e.preventDefault();
              selectDocument(documentSuggestions[selectedSuggestionIndex]);
            }
            break;
          case "Escape":
            setShowDocumentSuggestions(false);
            break;
        }
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();

        // Only submit if not currently loading
        if (!isLoading && message.trim()) {
          handleSubmit();
        }
      }
    };

    // Detect @ mentions in the text with debouncing for search queries
    const detectMention = useCallback(
      async (text: string, cursorPos: number) => {
        // Only enable @ mentions when document library is enabled
        if (!isDocumentLibraryEnabled) {
          setShowDocumentSuggestions(false);
          return;
        }

        const beforeCursor = text.substring(0, cursorPos);
        // Check if we're typing a new mention (not inside an existing one)
        const mentionMatch = beforeCursor.match(/@(\w*)$/);

        // Don't show suggestions if we're inside an existing mention @[...]
        const isInsideExistingMention = /\@\[[^\]]*$/.test(beforeCursor);

        if (mentionMatch && !isInsideExistingMention) {
          const searchTerm = mentionMatch[1];

          // Clear any pending debounced search
          if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
          }

          // If there's a search term, debounce the API call
          if (searchTerm) {
            log.info("Debouncing @ mention search", { searchTerm, delay: "300ms" });

            searchDebounceRef.current = setTimeout(async () => {
              try {
                log.info("Executing debounced @ mention search", { searchTerm });
                const results = await searchFiles(searchTerm, 10);
                setDocumentSuggestions(results);
                setShowDocumentSuggestions(true);
                setSelectedSuggestionIndex(0);
              } catch (error) {
                log.error("Failed to fetch document suggestions", { error });
                setShowDocumentSuggestions(false);
              }
            }, 300); // 300ms debounce
          } else {
            // No search term (just "@"), fetch recent files immediately without debounce
            try {
              log.info("Fetching recent files for @ mention (no debounce)");
              const results = await getRecentFiles(10);
              setDocumentSuggestions(results);
              setShowDocumentSuggestions(true);
              setSelectedSuggestionIndex(0);
            } catch (error) {
              log.error("Failed to fetch document suggestions", { error });
              setShowDocumentSuggestions(false);
            }
          }
        } else {
          // Clear debounce and hide suggestions
          if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
          }
          setShowDocumentSuggestions(false);
        }
      },
      [searchFiles, getRecentFiles, isDocumentLibraryEnabled]
    );

    /**
     * Create a placeholder File object from SharePoint metadata.
     * This shows immediately in the carousel while the real file downloads in the background.
     */
    const createPlaceholderFile = (spFile: SharePointFile): EnhancedFile => {
      // Create empty blob - placeholder has no actual content
      const blob = new Blob([], {
        type: spFile.mimeType ?? "application/octet-stream",
      });

      const file = new File([blob], spFile.name, {
        type: spFile.mimeType ?? "application/octet-stream",
        lastModified: spFile.modifiedAt?.getTime() ?? spFile.createdAt.getTime(),
      }) as EnhancedFile;

      // Add SharePoint metadata for tracking and display
      file.isSharePointFile = true;
      file.sharePointFileId = spFile.id;
      file.sharePointWebUrl = spFile.webUrl;
      file.isPendingDownload = true; // Mark as placeholder

      return file;
    };

    /**
     * Convert SharePoint file metadata to actual File object by downloading content.
     * Now delegates to service layer for clean separation of concerns.
     */
    const convertSharePointFileToFile = async (spFile: SharePointFile): Promise<EnhancedFile> => {
      try {
        log.info("Converting SharePoint file to File object", {
          fileId: spFile.id,
          fileName: spFile.name,
        });

        // Use service layer method - it handles download and conversion
        const file = (await downloadFileAsFile(spFile)) as EnhancedFile;

        // Add SharePoint metadata properties for tracking
        file.isSharePointFile = true;
        file.sharePointFileId = spFile.id;
        file.sharePointWebUrl = spFile.webUrl;
        file.isAlreadyDownloaded = true; // Skip upload simulation - already downloaded!

        log.info("Successfully converted SharePoint file", {
          fileId: spFile.id,
          fileName: spFile.name,
          fileSize: file.size,
        });

        return file;
      } catch (error) {
        log.error("Failed to convert SharePoint file", {
          fileId: spFile.id,
          fileName: spFile.name,
          error,
        });
        throw error;
      }
    };

    // Handle document selection from suggestions
    const selectDocument = async (document: SharePointFile) => {
      // Close suggestions immediately for instant UI feedback
      setShowDocumentSuggestions(false);

      const beforeCursor = message.substring(0, cursorPosition);
      const afterCursor = message.substring(cursorPosition);
      const mentionMatch = beforeCursor.match(/@(\w*)$/);

      if (mentionMatch) {
        // Keep the @ mention in the message
        const beforeMention = beforeCursor.substring(0, beforeCursor.lastIndexOf("@"));
        const formattedName = document.name.toLowerCase().replace(/\s+/g, "-");
        const newMessage = `${beforeMention}@[${formattedName}] ${afterCursor}`;
        setMessage(newMessage);

        // Add document to carousel with placeholder → download → replace pattern
        if (onFilesSelected && onRemoveFile) {
          // STEP 1: Show placeholder immediately (with loading spinner)
          const placeholderFile = createPlaceholderFile(document);
          const placeholderId = createFileId(placeholderFile);

          log.info("Adding placeholder for @ mention file", {
            documentId: document.id,
            placeholderId,
          });
          onFilesSelected([placeholderFile]);

          // STEP 2: Download real file in background
          try {
            log.info("Downloading SharePoint file in background", { documentId: document.id });
            const realFile = await convertSharePointFileToFile(document);

            // STEP 3: Replace placeholder with real file
            onRemoveFile(placeholderId); // Remove placeholder
            onFilesSelected([realFile]); // Add real file

            // Track that this SharePoint file is now attached
            setAttachedSharePointFileIds((prev) => new Set(prev).add(document.id));

            log.info("Successfully replaced placeholder with real file", {
              documentId: document.id,
              fileName: realFile.name,
            });
          } catch (error) {
            // Map error codes to user-friendly messages
            let errorMsg = `Failed to download "${document.name}"`;

            if (error instanceof Error && "statusCode" in error) {
              const spError = error as any; // SharePointApiError

              if (spError.statusCode === 403) {
                // Check specific error codes first
                if (spError.errorCode === "SHAREPOINT_CONSENT_REQUIRED") {
                  errorMsg = `SharePoint integration requires administrator approval. Please contact your IT administrator and share error code: SHAREPOINT_CONSENT_REQUIRED`;
                } else if (spError.errorCode === "SHAREPOINT_MFA_REQUIRED") {
                  errorMsg = `Multi-factor authentication required. Please log out and log back in to complete authentication.`;
                } else {
                  // Generic 403 (no error_code) - permission denied
                  errorMsg = `You don't have permission to download "${document.name}". You can see it but cannot access its contents. Contact your SharePoint administrator.`;
                }
              } else if (spError.statusCode === 404) {
                errorMsg = `"${document.name}" not found or you don't have access to it.`;
              } else if (spError.statusCode === 401) {
                errorMsg = `Session expired. Please refresh the page to continue.`;
              } else if (spError.statusCode === 429) {
                const retryAfter = spError.details?.retry_after || 60;
                errorMsg = `Too many download requests. Please wait ${retryAfter} seconds and try again.`;
              } else if (spError.errorCode === "EMPTY_FILE") {
                errorMsg = `"${document.name}" download failed (file may be empty or corrupted). Please try again.`;
              }
            }

            setDownloadError(errorMsg);
            setTimeout(() => setDownloadError(null), 5000); // Auto-dismiss after 5s
            log.error("Failed to download SharePoint file", {
              documentId: document.id,
              error,
            });
            // Remove failed placeholder
            onRemoveFile(placeholderId);
          }
        }

        setSelectedDocuments([...selectedDocuments, document]);
        log.info("Document selected via @ mention", {
          documentId: document.id,
          documentName: document.name,
          formattedName: formattedName,
        });
      }
    };

    // Handle documents selected from library modal
    const handleLibraryDocumentsSelected = async (documents: SharePointFile[]) => {
      // Add documents to carousel with placeholder → download → replace pattern
      if (onFilesSelected && onRemoveFile) {
        // STEP 1: Show placeholders immediately (with loading spinners)
        const placeholders = documents.map((doc) => createPlaceholderFile(doc));
        const placeholderIds = placeholders.map((file) => createFileId(file));

        log.info("Adding placeholders for library modal files", {
          count: documents.length,
          placeholderIds,
        });
        onFilesSelected(placeholders);

        // STEP 2: Download real files in background (parallel)
        const downloadPromises = documents.map(async (doc, index) => {
          try {
            log.info("Downloading SharePoint file from modal", { documentId: doc.id });
            const realFile = await convertSharePointFileToFile(doc);

            // STEP 3: Replace placeholder with real file
            onRemoveFile(placeholderIds[index]); // Remove placeholder
            onFilesSelected([realFile]); // Add real file

            log.info("Successfully replaced placeholder with real file", {
              documentId: doc.id,
              fileName: realFile.name,
            });

            return doc.id;
          } catch (error) {
            // Map error codes to user-friendly messages
            let errorMsg = `Failed to download "${doc.name}"`;

            if (error instanceof Error && "statusCode" in error) {
              const spError = error as any; // SharePointApiError

              if (spError.statusCode === 403) {
                // Check specific error codes first
                if (spError.errorCode === "SHAREPOINT_CONSENT_REQUIRED") {
                  errorMsg = `SharePoint integration requires administrator approval. Please contact your IT administrator and share error code: SHAREPOINT_CONSENT_REQUIRED`;
                } else if (spError.errorCode === "SHAREPOINT_MFA_REQUIRED") {
                  errorMsg = `Multi-factor authentication required. Please log out and log back in to complete authentication.`;
                } else {
                  // Generic 403 (no error_code) - permission denied
                  errorMsg = `You don't have permission to download "${doc.name}". You can see it but cannot access its contents. Contact your SharePoint administrator.`;
                }
              } else if (spError.statusCode === 404) {
                errorMsg = `"${doc.name}" not found or you don't have access to it.`;
              } else if (spError.statusCode === 401) {
                errorMsg = `Session expired. Please refresh the page to continue.`;
              } else if (spError.statusCode === 429) {
                const retryAfter = spError.details?.retry_after || 60;
                errorMsg = `Too many download requests. Please wait ${retryAfter} seconds and try again.`;
              } else if (spError.errorCode === "EMPTY_FILE") {
                errorMsg = `"${doc.name}" download failed (file may be empty or corrupted). Please try again.`;
              }
            }

            setDownloadError(errorMsg);
            setTimeout(() => setDownloadError(null), 5000); // Auto-dismiss after 5s
            log.error("Failed to download SharePoint file from modal", {
              documentId: doc.id,
              error,
            });
            // Remove failed placeholder
            onRemoveFile(placeholderIds[index]);
            return null;
          }
        });

        // Wait for all downloads and track successful ones
        const successfulIds = (await Promise.all(downloadPromises)).filter((id) => id !== null);
        setAttachedSharePointFileIds((prev) => {
          const newSet = new Set(prev);
          successfulIds.forEach((id) => newSet.add(id as string));
          return newSet;
        });

        log.info("Finished downloading all files from library modal", {
          total: documents.length,
          successful: successfulIds.length,
        });
      }

      setSelectedDocuments([...selectedDocuments, ...documents]);
      log.info("Documents selected from library modal", {
        count: documents.length,
        documents: documents.map((d) => ({ id: d.id, name: d.name })),
      });
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart || 0;
      setMessage(newValue);
      setCursorPosition(cursorPos);
      detectMention(newValue, cursorPos);
    };

    /**
     * Agent selection handler using context
     *
     * This delegates to the agent context which maintains the single source of truth
     * for the current agent across the entire application.
     */
    const handleAgentSelection = (agent: Agent) => {
      log.info("Agent selected via ModernChatInput", {
        previousAgent: currentAgent.id,
        newAgent: agent.id,
      });
      setCurrentAgent(agent);
    };

    return (
      <div style={{ ...modernChatStyles.inputContainer, position: "relative" }}>
        {/* Insert button - temporarily commented out TODO: We need to decide if we want to keep this or not
      {canInsert && onInsert && (
        <div style={modernChatStyles.insertButtonContainer}>
          <button 
            style={modernChatStyles.insertButton}
            onClick={onInsert}
            disabled={isInserting}
          >
            {isInserting ? "Inserting..." : "Insert Last Response into Document"}
          </button>
        </div>
      )}
      */}

        {/* Document suggestions dropdown - shown when @ is typed */}
        {showDocumentSuggestions && documentSuggestions.length > 0 && (
          <DocumentSuggestions
            suggestions={documentSuggestions}
            selectedIndex={selectedSuggestionIndex}
            onSelect={selectDocument}
            position={{ top: 60, left: 0 }}
          />
        )}

        {/* Textarea with ref and additional styles for smooth transition */}
        <textarea
          ref={textareaRef}
          style={{
            ...(modernChatStyles.inputField as React.CSSProperties),
            transition: "height 0.1s ease",
            overflow: "auto",
            resize: "none",
          }}
          value={message}
          onChange={handleChange}
          placeholder={placeholder}
          rows={1}
          onKeyDown={handleKeyDown}
        />

        {/* Action Bar */}
        <div style={modernChatStyles.actionBar}>
          {/* Left side - New menu buttons */}
          <div
            style={{
              ...modernChatStyles.actionBarLeft,
            }}
          >
            {showClearChat && (
              <>
                <PlusMenuButton
                  onNewConversation={onClearChat}
                  onFilesSelected={onFilesSelected} // NEW: Pass file selection callback to PlusMenuButton
                  onBrowseLibrary={() => setShowLibraryModal(true)}
                />
                <SettingsMenuButton isStreaming={isLoading} />
                {/* Concise Mode Indicator Badge - the badge only turns the mode
                    OFF (it renders only while active). The setting is snapshotted
                    into each request at send time, so toggling during streaming
                    never affects the in-flight response - the event's
                    during_streaming property tracks how often users try that. */}
                {isConciseModeEnabled && (
                  <ConciseModeBadge
                    conciseMode={settings.conciseMode}
                    onToggle={() => {
                      track("settings:toggled", {
                        setting: "concise_mode",
                        enabled: !settings.conciseMode,
                        during_streaming: isLoading,
                        source: "badge",
                      });
                      toggleConciseMode();
                    }}
                  />
                )}
                {/* Catylex Partner Logo */}
                {FEATURE_FLAGS.ENABLE_CATYLEX_LOGO && <CatylexLogo height={24} />}
                {/* Tenant Selector for multi-tenant testing */}
                {showTenantSelector && <TenantSelector />}
                {/* Benchmark Mode Indicator Badge */}
                {FEATURE_FLAGS.ENABLE_BENCHMARK_MODE && (
                  <BenchmarkBadge
                    benchmarkMode={settings.benchmarkMode}
                    onToggle={toggleBenchmarkMode}
                  />
                )}
              </>
            )}
          </div>

          {/* Right side - Clean agent selector and send button */}
          <div style={modernChatStyles.actionBarRight}>
            <AgentSelector
              currentAgent={currentAgent}
              agents={availableAgents} // FIXED: Now using availableAgents from context
              onSelectAgent={handleAgentSelection}
            />

            <button
              style={{
                ...modernChatStyles.sendButton,
                opacity: !message.trim() && !isLoading ? 0.6 : 1,
                cursor: isLoading ? "pointer" : !message.trim() ? "not-allowed" : "pointer",
              }}
              onClick={isLoading ? cancelGeneration : handleSubmit}
              title={isLoading ? "Stop generating" : "Send message"}
              disabled={!isLoading && !message.trim()}
            >
              {isLoading ? (
                <StopGeneratingIcon width="18" height="18" style={{ display: "block" }} />
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22 2L11 13"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 2L15 22L11 13L2 9L22 2Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Document Library Modal */}
        <DocumentLibraryModal
          isOpen={showLibraryModal}
          onClose={() => setShowLibraryModal(false)}
          onSelectDocuments={handleLibraryDocumentsSelected}
          currentlyAttachedFileIds={attachedSharePointFileIds}
        />

        {/* Error Toast - minimal inline implementation */}
        {downloadError && (
          <div
            style={{
              position: "fixed",
              bottom: 20,
              right: 20,
              background: "#dc3545",
              color: "white",
              padding: "12px 20px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              zIndex: 10000,
              maxWidth: "400px",
              fontSize: "14px",
              fontFamily: "'Segoe UI', sans-serif",
            }}
          >
            {downloadError}
          </div>
        )}
      </div>
    );
  }
);

ModernChatInput.displayName = "ModernChatInput";

export default ModernChatInput;
